import { Component, computed, inject, input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { EMatchingType } from '@enums/campaign-analytics.enum';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { ECampaignDetailsTab } from '@pages/campaigns/campaign-details/campaign-details.interface';
import { CampaignDetailsStore } from '@pages/campaigns/campaign-details/campaign-details.store';
import { CtaAnalyticsDialogComponent } from '@pages/campaigns/campaign-details/cta-analytics-dialog/cta-analytics-dialog.component';
import { ICtaAnalyticsDialogData } from '@pages/campaigns/campaign-details/cta-analytics-dialog/cta-analytics-dialog.interface';
import { SocialContentTilesListComponent } from '@pages/campaigns/campaign-details/social-content-tiles-list/social-content-tiles-list.component';
import { ITileConfiguration } from '@shared/components/social-content-tile/social-content-tile.interface';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';

@Component({
  imports: [SocialContentTilesListComponent],
  selector: 'app-campaign-details-creator-timeline',
  templateUrl: './campaign-details-creator-timeline.component.html',
})
export class CampaignDetailsCreatorTimelineComponent {
  private readonly store = inject(CampaignDetailsStore);
  private readonly dialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);

  public readonly adminMode = this.route.snapshot.data['adminMode'] === true;
  public readonly creatorBriefingMode = this.route.snapshot.data['creatorBriefingMode'] === true;

  public readonly analyticsNodes = input<IAnalyticsNode[] | null>(null);
  public readonly resetToPlanned = input<number>(0);

  public readonly sortedNodes = computed(() => {
    const nodes = this.analyticsNodes();

    if (!nodes) {
      return nodes;
    }

    return this.sortNodes(nodes);
  });

  public readonly loading = computed(() => {
    const state = this.store.deliverablesState$();

    return state.inFlight || state.data === null;
  });

  private readonly briefingId = this.route.snapshot.params['briefingId'] ?? null;

  public onTileClick(tileConfiguration: ITileConfiguration): void {
    const analyticsNode = this.store.deliverablesState$().data?.find((node) => node.id === tileConfiguration.id);

    if (!analyticsNode || !this.store.campaignAnalyticsShell$() || !this.store.campaignApi$()) {
      return;
    }

    const dialogData: ICtaAnalyticsDialogData = {
      adminMode: this.adminMode,
      analyticsNode,
      briefingId: this.briefingId,
      campaign: this.store.campaignApi$()!,
      campaignAnalytics: this.store.campaignAnalyticsShell$()!,
      campaignExport: this.store.campaignExport$()!,
      creatorBriefingMode: this.creatorBriefingMode,
      currentTab: toEventFrontendTab(ECampaignDetailsTab.creatorTimeline),
      exportMode: false,
      selectedCtaId: tileConfiguration.selectedCtaId ?? null,
    };

    this.dialog.open(CtaAnalyticsDialogComponent, {
      data: dialogData,
      maxWidth: '1800px',
      width: '90%',
    });
  }

  private sortNodes(nodes: IAnalyticsNode[]): IAnalyticsNode[] {
    const now = Date.now();

    return [...nodes].sort((a, b) => {
      const groupA = this.getSortGroup(a, now);
      const groupB = this.getSortGroup(b, now);

      if (groupA !== groupB) {
        return groupA - groupB;
      }

      return this.getSecondarySortValue(a, groupA, now) - this.getSecondarySortValue(b, groupB, now);
    });
  }

  /**
   * Group 0 — planned + always
   * Group 1 — planned + unique with future/today date (not passed)
   * Group 2 — planned + undesignated
   * Group 3 — missing with a past execution date
   * Group 4 — missing without date
   * Group 5 — matched / bonus
   */
  private getSortGroup(node: IAnalyticsNode, now: number): number {
    const executionType = node.plannedExecution?.executionType ?? null;
    const matchingType = node.executionStatus;

    if (matchingType === EMatchingType.planned) {
      if (executionType === EExecutionType.always) return 0;

      if (executionType === EExecutionType.unique) return 1;

      if (executionType === EExecutionType.undesignated) return 2;

      return 2;
    }

    if (matchingType === EMatchingType.missing) {
      const ts = this.getExecutionTimestamp(node);

      return ts !== null && ts < now ? 3 : 4;
    }

    // matched, bonus, unrelated
    return 5;
  }

  private getSecondarySortValue(node: IAnalyticsNode, group: number, now: number): number {
    const ts = this.getExecutionTimestamp(node);

    if (group === 1) {
      // Upcoming unique: closest future date first → distance from now ascending
      return ts !== null ? ts - now : Number.MAX_SAFE_INTEGER;
    }

    if (group === 3) {
      // Past missing: most recently passed first → descending ts → negate
      return ts !== null ? -ts : 0;
    }

    if (group === 5) {
      // matched/bonus: closest date first
      return ts !== null ? Math.abs(ts - now) : Number.MAX_SAFE_INTEGER;
    }

    // group 0 (always), group 2 (undesignated), group 4 (missing no date): stable, preserve original order
    return 0;
  }

  private getExecutionTimestamp(node: IAnalyticsNode): number | null {
    const raw = node.executionTime;

    if (!raw) {
      return null;
    }

    const d = new Date(raw);

    return isNaN(d.getTime()) ? null : d.getTime();
  }
}
